import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import EditableTable from '../../../elem/table/EditableTable'
import { parseUploadColumns } from '../../../../utils/table/parseColumns'
import { DataContext } from '../DataContext'
import SectionWrapper from './SectionWrapper'
import GeneralErrors from './GeneralErrors'
import deepClone from 'lodash.clonedeep'
import PageErrorText from './PageErrorText'
import { useFormContext } from 'react-hook-form'
import ConfirmDeleteRecord from './ConfirmDeleteRecord'
import deleteFunction from '../table/deleteFunction'
import { decapitalizeText } from '../../../../utils/stringUtils'

const getInputNameFromConfig = (config, fieldName) => {
    if (!config) {
        return fieldName
    }
    const name = decapitalizeText(config.ColumnName)
    switch (config.ControlType) {
        case 'TextBox':
            return name
        case 'Select':
            return `${name}Select`
        case 'MultiSelect':
            return `${name}Select`
        case 'DateDayPicker':
            return `${name}DateSelect`
        case 'DatePicker':
            return `${name}DateSelect`
        case 'Creatable':
            return `${name}Select`
        default:
            return name
    }
}

const getDataFromSubmissionState = (submissionState, accessor) =>
    submissionState && submissionState[accessor]
        ? submissionState[accessor]
        : []

export default ({ config, accessor, title, subtitle }) => {
    const {
        submissionState,
        formMethods,
        viewOnly,
        setTableData,
        setFormDirty,
        updateControlledPageSize,
        pageErrorText,
        beforePageChange,
        displayDeleteModal,
        setDisplayDeleteModal,
        errorState,
        setErrorState,
    } = useContext(DataContext)

    const removeAndShiftErrorState = useCallback((deletedRecordaccessor, rowIdx) => {
        if (!errorState || Object.keys(errorState).length === 0) {
            return null
        }
        const dummyErrorState = errorState
        const deletedRowName = `${deletedRecordaccessor}[${rowIdx}]`
        delete dummyErrorState[deletedRowName]
        let elementsAfterDeletedRow = {}
        let unchangedElements = {}
        for (const rowName in dummyErrorState) {
            const rowAccessor = rowName.split('[')
            if (rowAccessor.length > 1) {
                const position = rowAccessor[1].replace(']', '')
                const rowData = dummyErrorState[rowName]
                if (position >= rowIdx && deletedRecordaccessor === rowAccessor[0]) {
                    delete dummyErrorState[rowName]
                    const formattedRowName = `${rowAccessor[0]}[${position - 1}]`
                    elementsAfterDeletedRow = { ...elementsAfterDeletedRow, [formattedRowName]: rowData }
                } else {
                    unchangedElements = { ...unchangedElements, [rowName]: rowData }
                }
            }
        }
        setErrorState({...unchangedElements, ...elementsAfterDeletedRow})
    }, [errorState])

    const { setValue } = useFormContext()

    const [data, setData] = useState(getDataFromSubmissionState(submissionState, accessor))
    
    const columns = useMemo(
        () => 
            parseUploadColumns(
                data,
                config,
                submissionState,
                viewOnly,
                setDisplayDeleteModal
            )
        , [config, data, formMethods, accessor, submissionState, viewOnly]
    )

    useEffect(() => {
        setData(getDataFromSubmissionState(submissionState, accessor))
    }, [config, submissionState])

    useEffect(() => {
        if (data.length) {
            const lastRowIdx = data.length - 1
            const lastRow = data[lastRowIdx]
            if (!Object.keys(lastRow).some(x => lastRow[x] !== null)) {
                // TODO: When we add a row, make sure the form data is cleared
                Object.keys(lastRow).map(key => {
                    const associatedConfig = config.find(x => x.ControlName === key)
                    const fieldName = getInputNameFromConfig(associatedConfig, key) 
                    const formName = `${accessor}[${lastRowIdx}].${fieldName}`

                    // hack for the id keys in dependent tables
                    if (formName.includes('ID') && !formName.includes('Select')) {
                        setValue(formName.replace('ID', 'IDSelect'), null, { shouldDirty: true })
                    }
                    return setValue(formName, null, { shouldDirty: true })
                })
            }
        }
    }, [data, submissionState, accessor, setValue, config])


    useEffect(() => {
        setTableData(prevTableData => {
            const newTableData = deepClone(prevTableData)
            newTableData[accessor] = data
            return newTableData
        })
    }, [data, accessor])
    
    const boundDeleteFunction = useMemo(() => 
        deleteFunction.bind(
            this,
            setData,
            formMethods,
            accessor,
            setFormDirty,
            columns.configsWithDependentFields,
            removeAndShiftErrorState,
        ), [
        setData,
        formMethods,
        accessor,
        setFormDirty,
        columns,
        removeAndShiftErrorState
    ])

    const tableData = useMemo(() => data, [data])

    return (
        <SectionWrapper title={title} subtitle={subtitle}>
            <PageErrorText string={pageErrorText[accessor]} />
            <GeneralErrors />
            <div className="formTableWrapper">
                <ConfirmDeleteRecord
                    displayDeleteModal={displayDeleteModal}
                    setDisplayDeleteModal={setDisplayDeleteModal}
                    accessor={accessor}
                    deleteFunction={boundDeleteFunction}
                />
                <EditableTable
                    columns={columns.returnColumns}
                    data={tableData}
                    setData={!viewOnly ? setData : null}
                    setControlledPageSize={updateControlledPageSize.bind(this, accessor)}
                    beforePageChange={beforePageChange}
                />
            </div>
        </SectionWrapper>
    )
}
